/**
 * MessageList component - Scrollable message display with auto-scroll
 *
 * Features:
 * - Displays user and agent messages
 * - Auto-scrolls to bottom on new messages
 * - Welcome message and suggested queries when empty
 * - Smooth scroll behavior
 */

import { useEffect, useRef } from 'react'
import { ChatMessage, MessageRole, ToolStatus } from '../../types/chat'
import { FeedbackReaction } from '../../types/feedback'
import type { ReactionDispatch } from '../../lib/feedback'
import UserMessage from './UserMessage'
import AgentMessage from './AgentMessage'
import WelcomeMessage from './WelcomeMessage'
import SuggestedQueries from './SuggestedQueries'
import { MessageSkeleton } from '../LoadingSkeleton'

interface MessageWithReaction extends ChatMessage {
  reaction?: FeedbackReaction | null
}

interface MessageListProps {
  messages: MessageWithReaction[]
  onSendMessage?: (content: string) => void
  onPlanManeuver?: (cdmId: string) => void
  createReactionDispatch?: (messageId: string | null) => ReactionDispatch
  disabled?: boolean
}

function MessageList({
  messages,
  onSendMessage,
  onPlanManeuver,
  createReactionDispatch,
  disabled = false,
}: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Check if there's an agent message with running workflow steps
  const hasRunningWorkflow = messages.some(
    (msg) =>
      msg.role === MessageRole.AGENT &&
      msg.workflow_steps?.some((step) => step.status === ToolStatus.RUNNING)
  )

  if (messages.length === 0) {
    return (
      <div>
        <WelcomeMessage />
        {onSendMessage && (
          <SuggestedQueries onSelectQuery={onSendMessage} disabled={disabled} />
        )}
      </div>
    )
  }

  // Generate a unique key for each message
  // Combine message ID, timestamp, and index to ensure uniqueness
  const getMessageKey = (message: ChatMessage, index: number): string => {
    const timestamp = message.created_at || ''
    const role = message.role
    // Use message.id if unique, otherwise combine session_id + timestamp + index
    if (message.id) {
      return `${message.id}-${index}-${timestamp}`
    }
    return `${message.session_id}-${role}-${index}-${timestamp}`
  }

  return (
    <div className="space-y-6">
      {messages.map((message, index) => (
        <div key={getMessageKey(message, index)} className="message-item">
          {message.role === 'user' ? (
            <UserMessage message={message} />
          ) : (
            <AgentMessage
              message={message}
              reaction={message.reaction ?? null}
              createDispatch={createReactionDispatch}
              onPlanManeuver={onPlanManeuver}
              disabled={disabled}
            />
          )}
        </div>
      ))}

      {/* Show loading skeleton if workflow is running */}
      {hasRunningWorkflow && <MessageSkeleton />}

      {/* Invisible element for auto-scroll target */}
      <div ref={messagesEndRef} />
    </div>
  )
}

export default MessageList
